import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikState } from 'formik';
import { Form, Header, Message } from 'semantic';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import DateField from 'components/form-fields/formik/DateField';
import Divider from 'components/Sidebar/Divider';

import {
  TariffRule as CustomTariffRuleType,
  Tariff,
} from 'subdomains/cpo/types/charging-policy';
import { buildFormPath } from 'subdomains/cpo/utils/formpath';
import PaymentOptionSelection from './PaymentOptionSelection';

import { SetFieldValueFn } from 'subdomains/cpo/types/form';
import CustomerGroupSelection from './CustomerGroupSelection';
import { TariffFormFields } from '../../../../components/Tariff/TariffFormFields';
import InputField from 'components/form-fields/formik/InputField';

type Props = {
  values: FormikState<CustomTariffRuleType>['values'];
  setFieldValue: SetFieldValueFn<Tariff>;
  basePath?: string;
};

export const TariffRuleFormFields: React.FC<Props> = ({
  values,
  setFieldValue,
  basePath,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <InputField
        label={t(
          'providerAdmin.chargingPolicyForm.tariffLabelLabel',
          'Tariff name'
        )}
        name={buildFormPath('label', basePath)}
      />

      <TariffFormFields
        basePath={buildFormPath('tariff', basePath)}
        values={values?.tariff}
        setFieldValue={setFieldValue}
      />
      <div style={{ marginBottom: 30 }}>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingPolicyForm.tariffOptionsTitle',
            'Options'
          )}
        />
        <CheckboxField
          toggle
          name={buildFormPath('publishToRoaming', basePath)}
          label={t(
            'providerAdmin.chargingPolicyForm.publishToRoamingLabel',
            'Publish this tariff to the roaming network'
          )}
        />
        {values.publishToRoaming && (
          <div style={{ marginBottom: 40 }}>
            <Message warning>
              {t(
                'providerAdmin.chargingPolicyForm.publishToRoamingWarning',
                'Once the tariff fulfills certain requirements (e.g., is associated with at least one charging station), it will be published to the roaming network'
              )}
            </Message>
          </div>
        )}

        <CheckboxField
          toggle
          name={buildFormPath('publishToCSUI', basePath)}
          label={t(
            'providerAdmin.chargingPolicyForm.publishToCSUILabel',
            'Publish this tariff to CSUI'
          )}
        />

        <CheckboxField
          toggle
          name={buildFormPath('validityPeriod.enabled', basePath)}
          label={t(
            'providerAdmin.chargingPolicyForm.validityPeriodEnabledLabel',
            'This tariff has a validity period'
          )}
        />
      </div>
      {values?.validityPeriod?.enabled && (
        <>
          <p style={{ marginLeft: '4.6rem' }}>
            {t(
              'providerAdmin.chargingPolicyForm.validityPeriodDescription',
              'Ensure that your tariff components with date restrictions are within this tariff validity period.'
            )}
          </p>
          <>
            <Divider />
            <Form.Group>
              <DateField
                name={buildFormPath('validityPeriod.start', basePath)}
                label={t(
                  'providerAdmin.chargingPolicyForm.validityPeriodStartDate',
                  'Start date'
                )}
              />
              <DateField
                name={buildFormPath('validityPeriod.end', basePath)}
                label={t(
                  'providerAdmin.chargingPolicyForm.validityPeriodEndDate',
                  'End date'
                )}
              />
            </Form.Group>
          </>
        </>
      )}

      <div style={{ marginTop: 30, marginBottom: 30 }}>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingPolicyForm.paymentOptionsTitle',
            'Payment options this tariff applies to'
          )}
        />
        <PaymentOptionSelection
          values={values?.paymentOptions}
          onChange={(values) =>
            setFieldValue(buildFormPath('paymentOptions', basePath), values)
          }
        />
      </div>
      <div style={{ marginBottom: 30 }}>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingPolicyForm.customerGroupsTitle',
            'Who does this tariff apply to'
          )}
        />
        <CustomerGroupSelection
          values={values?.customerGroups}
          onChange={(values) =>
            setFieldValue(buildFormPath('customerGroups', basePath), values)
          }
        />
      </div>
    </>
  );
};
